import { RefObject, memo, useCallback, useEffect, useRef, useState } from 'react';

import type { AgGridEvent, CellClickedEvent, GridOptions, IRowNode, SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

import { NoRowsStatusType, type TypeNoRowsStatusKey } from '~/constants';

import { NoRowsOverlay } from '../NoRowsOverlay';

import styles from './Table.module.scss';

const DEFAULT_COL_MIN_WIDTH = 49;

const DEFAULT_COL_DEF = {
  wrapText: true,
  resizable: true,
  suppressMovable: true,
  sortable: false,
  minWidth: DEFAULT_COL_MIN_WIDTH,
  cellStyle: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  valueFormatter: ({ value }: { value?: string }) => {
    if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
      return '-';
    } else {
      return value;
    }
  },
};

const DEFAULT_GRID_OPTIONS: GridOptions = {
  defaultColDef: DEFAULT_COL_DEF,
  rowSelection: 'multiple',
  suppressRowClickSelection: true,
  suppressRowTransform: true,
  suppressCellFocus: true,
  suppressRowVirtualisation: true,
  alwaysShowHorizontalScroll: true,
  enableCellTextSelection: true,
  headerHeight: 44,
  rowHeight: 40,
  noRowsOverlayComponent: NoRowsOverlay,
  reactiveCustomComponents: true,
  domLayout: 'normal',
};

type TypeTableProps<TypeData> = {
  data: TypeData[] | null;
  options: GridOptions;
  gridEvents?: AgGridEvent<TypeData>[];
  className?: string;
  rowStatusType?: TypeNoRowsStatusKey | null;
  gridRef?: RefObject<AgGridReact>;
  onSelectionChange?: (selectedRows: IRowNode<TypeData>[]) => void;
  style?: React.CSSProperties;
};

const Table = <TypeData,>({
  data,
  options,
  gridEvents,
  className,
  rowStatusType = NoRowsStatusType.EMPTY,
  gridRef,
  onSelectionChange,
  style,
  ...props
}: TypeTableProps<TypeData>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [rowData, setRowData] = useState(data ?? []);
  const [gridOptions, setGridOptions] = useState<GridOptions>({
    ...DEFAULT_GRID_OPTIONS,
    ...(rowStatusType ? { noRowsOverlayComponentParams: { rowStatusType } } : {}),
    ...options,
    defaultColDef: { ...DEFAULT_COL_DEF, ...options.defaultColDef },
  });
  const [onGridEvents, setOnGridEvents] = useState<AgGridEvent<TypeData>[] | undefined>(gridEvents);

  useEffect(() => {
    setRowData(data ?? []);
  }, [data]);

  useEffect(() => {
    setGridOptions((prev) => ({
      ...prev,
      ...options,
      noRowsOverlayComponentParams: { rowStatusType },
    }));
  }, [options, rowStatusType]);

  useEffect(() => {
    setOnGridEvents(gridEvents);
  }, [gridEvents]);

  const handleSelectionChanged = useCallback((e: SelectionChangedEvent<TypeData>) => {
    const selectedRows = e.api.getSelectedNodes();

    onSelectionChange?.(selectedRows);
  }, []);

  const handleCellClicked = useCallback((e: CellClickedEvent<TypeData>) => {
    const isCellSelectable = e.colDef.headerCheckboxSelection && e.colDef.checkboxSelection;

    if (isCellSelectable) {
      const isSelected = e.node.isSelected();
      e.node.setSelected(!isSelected);
    }
  }, []);

  const getRowSelectionClass = useCallback(() => {
    const selectionType = gridOptions.rowSelection || 'multiple';
    return `ag-row-selection-${selectionType}`;
  }, [gridOptions.rowSelection]);

  return (
    <div
      ref={ref}
      className={`ag-theme-quartz ${styles['table']} ${getRowSelectionClass()} ${className ?? ''}`}
      style={style}
    >
      <AgGridReact<TypeData>
        ref={gridRef}
        rowData={rowData}
        {...gridOptions}
        {...onGridEvents}
        {...props}
        onSelectionChanged={handleSelectionChanged}
        onCellClicked={handleCellClicked}
      />
    </div>
  );
};

const MemoizedTable = memo(Table) as typeof Table;
export default MemoizedTable;
